import {
  getOnlineDeviceList,
  getPublishedCampaignToFront,
  pushCampaignToOnlineDevice,
} from '@/services/campaign'
import { Device } from '@/services/device'
import { Button, Card, message, Modal, Select, Space, Table, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import React, { useEffect, useState } from 'react'

const { Title } = Typography
const { Option } = Select

interface PublishedCampaignOption {
  campaignId: string
  campaignName: string
  lastUpdated: string
}

const ForcedUpgrade: React.FC = () => {
  const [onlineDevices, setOnlineDevices] = useState<any[]>([])
  const [loading, setLoading] = useState(false)

  const [isModalVisible, setIsModalVisible] = useState(false)
  const [campaignList, setCampaignList] = useState<PublishedCampaignOption[]>([])
  const [selectedCampaignId, setSelectedCampaignId] = useState<string>()
  const [currentDevice, setCurrentDevice] = useState<Device | null>(null)
  const [upgradeLoading, setUpgradeLoading] = useState(false)

  // 获取在线车辆列表
  const fetchOnlineDevices = async () => {
    try {
      setLoading(true)
      const list = await getOnlineDeviceList()
      if (Array.isArray(list)) {
        const mapped = list.map((item: any) => ({
          sn: item.sn || '-',
          ipAddress: item.ipAddress,
          lastUpdatedTime: Date.now(),
        }))
        setOnlineDevices(mapped)
      } else {
        setOnlineDevices([])
      }
    } catch (error: any) {
      message.error(error?.message || '获取在线车辆失败')
    } finally {
      setLoading(false)
    }
  }

  // 初始化及轮询
  useEffect(() => {
    fetchOnlineDevices()
    const timer = setInterval(() => {
      fetchOnlineDevices()
    }, 15000)
    return () => clearInterval(timer)
  }, [])

  // 打开强制升级弹窗
  const openUpgradeModal = async (device: Device) => {
    try {
      setCurrentDevice(device)
      const res = await getPublishedCampaignToFront()
      if (res.code === '200') {
        setCampaignList(res.data || [])
        setIsModalVisible(true)
      } else {
        message.error(res.info || '获取可升级Campaign失败')
      }
    } catch (error: any) {
      message.error(error?.message || '获取可升级Campaign失败')
    }
  }

  // 确认升级
  const handleUpgrade = async () => {
    if (!selectedCampaignId) {
      message.error('请选择Campaign')
      return
    }
    if (!currentDevice) return

    try {
      setUpgradeLoading(true)
      const res = await pushCampaignToOnlineDevice({
        sn: currentDevice.sn,
        campaignId: selectedCampaignId,
      })
      if (res && res.successSn) {
        message.success('升级指令已发送')
        setIsModalVisible(false)
        setSelectedCampaignId(undefined)
      } else {
        message.error('发送升级指令失败')
      }
    } catch (error: any) {
      message.error(error?.message || '发送升级指令失败')
    } finally {
      setUpgradeLoading(false)
    }
  }

  const columns: ColumnsType<any> = [
    {
      title: '序号',
      key: 'index',
      width: 80,
      render: (_: any, __: any, index: number) => index + 1,
    },
    { title: 'SN', dataIndex: 'sn', key: 'sn' },
    {
      title: 'IP地址',
      dataIndex: 'ipAddress',
      key: 'ipAddress',
    },
    {
      title: '最后更新时间',
      dataIndex: 'lastUpdatedTime',
      key: 'lastUpdatedTime',
      render: (text: string) => (text !== '-' ? new Date(text).toLocaleString() : '-'),
    },
    {
      title: '操作',
      key: 'action',
      width: 180,
      render: (_: any, record: Device) => (
        <Button type='link' onClick={() => openUpgradeModal(record)}>
          升级
        </Button>
      ),
    },
  ]

  return (
    <div className='forced-upgrade'>
      <div className='forced-upgrade-header'>
        <Title level={4}>在线车辆列表</Title>
      </div>
      <Card>
        <Table
          columns={columns}
          dataSource={onlineDevices}
          rowKey='sn'
          loading={loading}
          pagination={false}
        />
      </Card>

      <Modal
        title={`升级车辆${currentDevice ? '（' + currentDevice.sn + '）' : ''}`}
        open={isModalVisible}
        onOk={handleUpgrade}
        confirmLoading={upgradeLoading}
        onCancel={() => setIsModalVisible(false)}
        okText='确认升级'
        cancelText='取消'
        style={{ minHeight: 260 }}
        bodyStyle={{ minHeight: 160 }}
      >
        <Space direction='vertical' style={{ width: '100%', marginTop: 25 }}>
          <Select
            placeholder='请选择Campaign'
            value={selectedCampaignId as any}
            onChange={value => setSelectedCampaignId(value)}
            style={{ width: '100%' }}
          >
            {campaignList.map(item => (
              <Option value={item.campaignId} key={item.campaignId}>
                {item.campaignName}
              </Option>
            ))}
          </Select>
        </Space>
      </Modal>
    </div>
  )
}

export default ForcedUpgrade
